import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, Plus, X, RefreshCw, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Loose IPv4 / IPv6 / CIDR shape check (server does authoritative validation)
const isValidIp = (s) => {
  if (!s || s.length > 64) return false;
  return /^[0-9a-fA-F\.\:\/]+$/.test(s);
};

const IPWhitelistEditor = ({ allowedIps, onChange, token }) => {
  const list = Array.isArray(allowedIps) ? allowedIps : [];
  const [draft, setDraft] = useState('');
  const [myIp, setMyIp] = useState('');
  const [loadingIp, setLoadingIp] = useState(false);
  const [err, setErr] = useState('');

  const fetchMyIp = useCallback(async () => {
    setLoadingIp(true);
    setErr('');
    try {
      const res = await axios.get(`${API}/admin/my-ip`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setMyIp(res.data?.ip || '');
    } catch (e) {
      setErr('IP-ni gətirmək alınmadı');
    } finally {
      setLoadingIp(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMyIp();
  }, [fetchMyIp]);

  const addIp = (ipRaw) => {
    const ip = (ipRaw || '').trim();
    if (!ip) return;
    if (!isValidIp(ip)) {
      setErr(`Yanlış IP formatı: ${ip}`);
      return;
    }
    if (list.includes(ip)) {
      setErr('Bu IP artıq siyahıdadır');
      return;
    }
    onChange([...list, ip]);
    setDraft('');
    setErr('');
  };

  const removeIp = (ip) => {
    onChange(list.filter((x) => x !== ip));
  };

  const protectionOn = list.length > 0;

  return (
    <div data-testid="ip-whitelist-editor">
      <h3 className="admin-section-title flex items-center gap-2">
        <Shield size={16} /> Admin Panel IP məhdudiyyəti
      </h3>
      <p className="admin-section-desc">
        Admin paneli yalnız siyahıdakı IP ünvanlarından açıq olacaq. Siyahı boş qaldıqda — hər kəsə açıqdır
        (geriyə uyğun standart davranış).
      </p>

      {/* Status banner */}
      <div
        style={{
          background: protectionOn ? 'rgba(34,197,94,0.08)' : 'rgba(234,179,8,0.08)',
          border: `1px solid ${protectionOn ? 'rgba(34,197,94,0.3)' : 'rgba(234,179,8,0.3)'}`,
          color: protectionOn ? '#86efac' : '#fde68a',
          padding: '10px 14px',
          borderRadius: 10,
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
        }}
        data-testid="ip-whitelist-status"
      >
        <Info size={14} />
        {protectionOn ? (
          <span>
            Aktiv qoruma — <strong>{list.length}</strong> IP-yə icazə verilib. Bütün digər IP-lər
            avtomatik bloklanır.
          </span>
        ) : (
          <span>Qoruma deaktiv — admin paneli hazırda hər kəsə açıqdır.</span>
        )}
      </div>

      {/* Current IP helper */}
      <div
        style={{
          background: 'rgba(168, 85, 247, 0.06)',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          borderRadius: 10,
          padding: '12px 14px',
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>
          Sizin hazırkı IP-niz:
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <code
            style={{
              background: 'rgba(0,0,0,0.4)',
              padding: '6px 12px',
              borderRadius: 6,
              color: '#c4b5fd',
              fontFamily: 'monospace',
              fontSize: 13,
              minWidth: 120,
            }}
            data-testid="my-ip-display"
          >
            {loadingIp ? 'Yüklənir...' : myIp || 'naməlum'}
          </code>
          <button
            type="button"
            onClick={fetchMyIp}
            className="admin-btn-secondary"
            data-testid="refresh-my-ip-btn"
            title="Yenidən yüklə"
          >
            <RefreshCw size={14} />
          </button>
          {myIp && !list.includes(myIp) && (
            <button
              type="button"
              onClick={() => addIp(myIp)}
              className="admin-btn-primary"
              data-testid="add-my-ip-btn"
            >
              <Plus size={14} /> Mənim IP-mi əlavə et
            </button>
          )}
          {myIp && list.includes(myIp) && (
            <span style={{ fontSize: 12, color: '#86efac' }}>✓ Artıq icazəlidir</span>
          )}
        </div>
      </div>

      {/* Add new IP form */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <input
          type="text"
          className="karma-input"
          style={{ flex: 1, minWidth: 180, textAlign: 'left' }}
          placeholder="məs. 203.0.113.45 və ya 2001:db8::1"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addIp(draft);
            }
          }}
          data-testid="ip-draft-input"
        />
        <button
          type="button"
          onClick={() => addIp(draft)}
          className="admin-btn-primary"
          disabled={!draft.trim()}
          data-testid="ip-add-btn"
        >
          <Plus size={14} /> Əlavə et
        </button>
      </div>
      {err && (
        <div style={{ color: '#fca5a5', fontSize: 12, marginBottom: 10 }} data-testid="ip-error">
          {err}
        </div>
      )}

      {/* List of IPs */}
      <div className="sortable-list" style={{ marginTop: 6 }}>
        {list.length === 0 && (
          <div
            style={{
              padding: '24px 16px',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.4)',
              fontSize: 13,
              border: '1px dashed rgba(255,255,255,0.1)',
              borderRadius: 10,
            }}
            data-testid="ip-empty-state"
          >
            Hələ heç bir IP əlavə edilməyib. Qoruma deaktivdir.
          </div>
        )}
        {list.map((ip) => (
          <div
            key={ip}
            className="sortable-item"
            data-testid={`ip-row-${ip}`}
            style={{ justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Shield size={14} style={{ color: '#86efac' }} />
              <code style={{ color: '#e2e8f0', fontFamily: 'monospace', fontSize: 13 }}>{ip}</code>
              {ip === myIp && (
                <span
                  style={{
                    fontSize: 10,
                    padding: '1px 8px',
                    borderRadius: 10,
                    background: 'rgba(59,130,246,0.15)',
                    color: '#bfdbfe',
                    border: '1px solid rgba(59,130,246,0.3)',
                  }}
                >
                  Siz
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => removeIp(ip)}
              className="user-delete-btn"
              data-testid={`ip-remove-${ip}`}
              title="Sil"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 16,
          padding: '10px 14px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 10,
          fontSize: 11,
          color: 'rgba(255,255,255,0.45)',
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: 'rgba(255,255,255,0.65)' }}>İpucu:</strong> Saxla düyməsinə basmazdan əvvəl mütləq
        öz hazırkı IP-nizi siyahıya əlavə edin — əks halda dəyişiklik tətbiq olunan kimi siz də öz admin panelinizdən
        kənarlaşa bilərsiniz.
      </div>
    </div>
  );
};

export default IPWhitelistEditor;
